import React, {
  useState,
  useCallback,
  useMemo,
  useEffect,
  useRef,
} from 'react';
import styles from './employee-weekly-schedule.module.scss';
import { WeekDaysArray } from 'shared/consts/calendar.consts';
import { LeftArrowSvg } from 'shared/svg';
import { RightArrowSvg } from 'shared/svg';
import { IEmployeeDay } from 'shared/api/booking/interfaces/get-employees-month-schedule.interface';
import { MonthCalendarDayType } from 'shared/api/booking/interfaces/month-calendar-day-type.enum';
import * as dateFns from 'date-fns';

interface IEmployeeWeeklyScheduleProps {
  days: IEmployeeDay[];
  currentDate: Date;
  onDayClick?: (day: IEmployeeDay, dayDate: Date) => void;
}

interface WeekData {
  startDate: Date;
  days: IEmployeeDay[];
}

export const EmployeeWeeklySchedule: React.FC<IEmployeeWeeklyScheduleProps> = ({
  days,
  currentDate,
  onDayClick,
}) => {
  const getWeeks = useCallback((): WeekData[] => {
    const sortedDays = [...days].sort(
      (a, b) => new Date(a.currDate).getTime() - new Date(b.currDate).getTime()
    );

    if (sortedDays.length === 0) return [];

    const weeks: WeekData[] = [];
    let currentWeekStart: Date | null = null;
    let currentWeekDays: IEmployeeDay[] = [];

    sortedDays.forEach((day) => {
      const dayDate = new Date(day.currDate);

      const weekStart = dateFns.startOfWeek(dayDate, { weekStartsOn: 1 });

      if (
        currentWeekStart === null ||
        !dateFns.isSameWeek(dayDate, currentWeekStart, { weekStartsOn: 1 })
      ) {
        if (currentWeekStart !== null && currentWeekDays.length > 0) {
          weeks.push({ startDate: currentWeekStart, days: currentWeekDays });
        }
        currentWeekStart = weekStart;
        currentWeekDays = [day];
      } else {
        currentWeekDays.push(day);
      }
    });

    if (currentWeekDays.length > 0 && currentWeekStart) {
      weeks.push({ startDate: currentWeekStart, days: currentWeekDays });
    }

    return weeks;
  }, [days]);

  const weeks = useMemo(() => getWeeks(), [getWeeks]);

  const [currentWeekIndex, setCurrentWeekIndex] = useState(() => {
    if (!weeks.length) return 0;
    const targetDate = dateFns.startOfWeek(currentDate, { weekStartsOn: 1 });
    for (let i = 0; i < weeks.length; i++) {
      const weekStart = dateFns.startOfWeek(weeks[i].startDate, {
        weekStartsOn: 1,
      });
      if (dateFns.isSameDay(weekStart, targetDate)) {
        return i;
      }
    }
    return 0;
  });

  const prevCurrentDateRef = useRef(currentDate);

  useEffect(() => {
    if (prevCurrentDateRef.current !== currentDate) {
      setCurrentWeekIndex(0);
      prevCurrentDateRef.current = currentDate;
    }
  }, [currentDate]);

  const goToPrevWeek = () => {
    if (currentWeekIndex > 0) {
      setCurrentWeekIndex(currentWeekIndex - 1);
    }
  };

  const goToNextWeek = () => {
    if (currentWeekIndex < weeks.length - 1) {
      setCurrentWeekIndex(currentWeekIndex + 1);
    }
  };

  const getDayClassName = (type: MonthCalendarDayType): string => {
    switch (type) {
      case MonthCalendarDayType.DAY_OFF:
        return styles.day_off;
      case MonthCalendarDayType.OLD_DAY:
        return styles.day_old;
      case MonthCalendarDayType.VACATION:
        return styles.day_vacation;
      case MonthCalendarDayType.ADMINISTRATIVE:
        return styles.day_administrative;
      case MonthCalendarDayType.SICK_LEAVE:
        return styles.day_sick_leave;
      default:
        return '';
    }
  };

  const renderDays = (weekDays: IEmployeeDay[]) => {
    if (weekDays.length === 0) {
      return WeekDaysArray.map((_, index) => (
        <div key={index} className={`${styles.day} ${styles.day_empty}`}>
          <span className={styles.day__from}></span>
          <span className={styles.day__number}></span>
          <span className={styles.day__to}></span>
        </div>
      ));
    }

    const dayMap = new Map<number, IEmployeeDay>();
    weekDays.forEach((day) => {
      const dayDate = new Date(day.currDate);
      const relativeIndex = dayDate.getDay() === 0 ? 6 : dayDate.getDay() - 1;
      dayMap.set(relativeIndex, day);
    });

    return WeekDaysArray.map((_, index) => {
      const day = dayMap.get(index);
      const dayDate = day ? new Date(day.currDate) : null;
      const dayNumber = dayDate ? dateFns.getDate(dayDate) : null;

      if (!day || !dayDate) {
        return (
          <div key={index} className={`${styles.day} ${styles.day_empty}`}>
            <span className={styles.day__from}></span>
            <span className={styles.day__to}></span>
            <span className={styles.day__number}></span>
          </div>
        );
      }

      return (
        <div
          key={index}
          className={`${styles.day} ${getDayClassName(day.type)}`}
          onClick={() => onDayClick?.(day, dayDate)}
        >
          <span className={styles.day__from}>{day.from || ''}</span>
          <span className={styles.day__number}>{dayNumber}</span>
          <span className={styles.day__to}>{day.to || ''}</span>
        </div>
      );
    });
  };

  const currentWeek = weeks[currentWeekIndex];

  if (!currentWeek || currentWeek.days.length === 0) {
    return (
      <div className={styles.container}>
        <div className={styles.week__names}>
          {WeekDaysArray.map((name, index) => (
            <div key={index} className={styles.names__block}>
              {name}
            </div>
          ))}
        </div>
        <div className={styles.week__slider}>
          <div className={`${styles.arrow} ${styles.arrow_disabled}`}>
            <LeftArrowSvg />
          </div>
          <div className={styles.week__days_container}>
            <div className={styles.week__days}>
              {WeekDaysArray.map((_, index) => (
                <div
                  key={index}
                  className={`${styles.day} ${styles.day_empty}`}
                >
                  <span className={styles.day__from}></span>
                  <span className={styles.day__number}></span>
                  <span className={styles.day__to}></span>
                </div>
              ))}
            </div>
          </div>
          <div className={`${styles.arrow} ${styles.arrow_disabled}`}>
            <RightArrowSvg />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.week__names}>
        {WeekDaysArray.map((name, index) => (
          <div key={index} className={styles.names__block}>
            {name}
          </div>
        ))}
      </div>
      <div className={styles.week__slider}>
        <div
          className={`${styles.arrow} ${currentWeekIndex === 0 ? styles.arrow_disabled : ''}`}
          onClick={goToPrevWeek}
        >
          <LeftArrowSvg />
        </div>
        <div className={styles.week__days_container}>
          <div className={styles.week__days}>
            {renderDays(currentWeek.days)}
          </div>
        </div>
        <div
          className={`${styles.arrow} ${currentWeekIndex >= weeks.length - 1 ? styles.arrow_disabled : ''}`}
          onClick={goToNextWeek}
        >
          <RightArrowSvg />
        </div>
      </div>
    </div>
  );
};
